import { Can } from '@/domains/access-control';
import { PageHeader } from '@/shared/components/PageHeader';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Clock, Loader2, Pencil, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import type {
  AssignShiftInput,
  AssignShiftToTeamInput,
  ShiftDetail,
  TeamAssignmentResult,
  UpdateShiftInput,
} from '../types/shift';
import { AssignToTeamModal } from './AssignToTeamModal';
import { AssignToUserModal } from './AssignToUserModal';
import { EditShiftModal } from './EditShiftModal';
import { type RemoveTeamAssignmentArgs, ShiftAssignments } from './ShiftAssignments';
import { ShiftStats } from './ShiftStats';

interface ShiftDetailViewProps {
  shift: ShiftDetail | null;
  isLoading?: boolean;
  onBack: () => void;
  onUpdateShift?: (data: UpdateShiftInput) => Promise<void>;
  onAssignShift?: (data: AssignShiftInput) => Promise<void>;
  onAssignShiftToTeam?: (data: AssignShiftToTeamInput) => Promise<TeamAssignmentResult>;
  onDeleteAssignment?: (id: number) => Promise<void>;
  onRemoveTeamAssignment?: (data: RemoveTeamAssignmentArgs) => Promise<void>;
}

export function ShiftDetailView({
  shift,
  isLoading = false,
  onBack,
  onUpdateShift,
  onAssignShift,
  onAssignShiftToTeam,
  onDeleteAssignment,
  onRemoveTeamAssignment,
}: ShiftDetailViewProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);

  const handleDeleteAssignment = async (assignmentId: number) => {
    try {
      await onDeleteAssignment?.(assignmentId);
      showSuccessToast('Assignment Removed', 'Shift assignment removed successfully.');
    } catch (error: unknown) {
      showErrorToast(
        'Failed to Remove Assignment',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
      );
    }
  };

  const handleRemoveTeamAssignment = async (data: RemoveTeamAssignmentArgs) => {
    try {
      await onRemoveTeamAssignment?.(data);
      showSuccessToast('Assignment Removed', 'Team shift assignment removed successfully.');
    } catch (error: unknown) {
      showErrorToast(
        'Failed to Remove Assignment',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
      );
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Shift not found</p>
          <Button variant="outline" className="mt-4" onClick={onBack}>
            Back to Shifts
          </Button>
        </div>
      </div>
    );
  }

  const assignments = shift.assignments ?? [];

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden">
        <PageHeader
          title={shift.title}
          subtitle={`${shift.startTime} - ${shift.endTime}`}
          icon={<Clock className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
          actions={
            <Can action="update" subject="Shift">
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowEditModal(true)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Shift
                </Button>
                <Button variant="outline" onClick={() => setShowAssignModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Assign to User
                </Button>
                <Button
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setShowAssignTeamModal(true)}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Assign to Team
                </Button>
              </div>
            </Can>
          }
        />

        <div className="flex-1 overflow-y-auto">
          <ShiftStats
            code={shift.code}
            startTime={shift.startTime}
            endTime={shift.endTime}
            color={shift.color}
          />

          <ShiftAssignments
            assignments={assignments}
            onRemoveTeamAssignment={handleRemoveTeamAssignment}
            onDeleteAssignment={handleDeleteAssignment}
            onAssignToTeam={() => setShowAssignTeamModal(true)}
          />
        </div>
      </div>

      {onUpdateShift && (
        <EditShiftModal
          open={showEditModal}
          shift={shift}
          onClose={() => setShowEditModal(false)}
          onSubmit={onUpdateShift}
        />
      )}

      {onAssignShift && (
        <AssignToUserModal
          open={showAssignModal}
          shiftId={shift.id}
          onClose={() => setShowAssignModal(false)}
          onAssign={onAssignShift}
        />
      )}

      {onAssignShiftToTeam && (
        <AssignToTeamModal
          open={showAssignTeamModal}
          shiftId={shift.id}
          onClose={() => setShowAssignTeamModal(false)}
          onAssign={onAssignShiftToTeam}
        />
      )}
    </>
  );
}
